import { cn } from "@/lib/utils";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { ComponentProps, useState } from "react";

import { ContentCard } from "./ContentCard";
import {
    ContentAbstract,
    ContentBookmark,
    ContentCardLayout,
    ContentSourceAuthorHeader,
    ContentTags,
    ContentThumbnailImg,
    ContentTitle
} from "./ContentCardLayout";

const ThumnailBox = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={cn(
                "relative mb-3 h-[180px] w-full overflow-hidden rounded-xl",
                className
            )}
            {...props}
        />
    );
};

const ThumnailOverlay = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={cn("absolute h-full w-full rounded-xl", className)}
            {...props}
        />
    );
};

export const VerticalHalfCard = ({
    content,
    interaction,
    className,
    priority = false,
    ...props
}: Omit<ComponentProps<typeof ContentCard>, "type" | "content"> & {
    content: Mix_ContentFragment;
    className?: string;
    priority?: boolean;
}) => {
    const { onClick, ...layoutProps } = props;
    const { isFeatureEnabled } = useFeatureFlags();
    const [showFallback, setShowFallback] = useState(!content.imgurl);
    const [isHovered, setIsHovered] = useState(false);
    const showPersonalFeed = isFeatureEnabled("HOME_PAGE_PERSONAL_FEED");
    const showBookmark =
        isHovered || interaction === "hover" || !!content.bookmarked;

    return (
        <ContentCardLayout
            {...layoutProps}
            className={cn(
                "group flex flex-col",
                "min-[1200px]:h-[370px]",
                `[&_.thumbnail-overlay]:bg-cover
                [&_.thumbnail-overlay]:bg-center
                [&_.thumbnail-overlay]:bg-no-repeat`,
                showFallback && "[&_.thumbnail-overlay]:hidden",
                `hover:[&_.thumbnail-overlay]:bg-gradient-to-t
                hover:[&_.thumbnail-overlay]:from-black/20
                hover:[&_.thumbnail-overlay]:to-black/20
                hover:[&_.thumbnail-overlay]:bg-cover
                hover:[&_.thumbnail-overlay]:bg-center
                hover:[&_.thumbnail-overlay]:bg-no-repeat`,
                `[&[data-interaction='hover']_.thumbnail-overlay]:bg-gradient-to-t
                [&[data-interaction='hover']_.thumbnail-overlay]:from-black/20
                [&[data-interaction='hover']_.thumbnail-overlay]:to-black/20
                [&[data-interaction='hover']_.thumbnail-overlay]:bg-cover
                [&[data-interaction='hover']_.thumbnail-overlay]:bg-center
                [&[data-interaction='hover']_.thumbnail-overlay]:bg-no-repeat`,
                `[&[data-bookmarked='true']_.thumbnail-overlay]:bg-gradient-to-t
                [&[data-bookmarked='true']_.thumbnail-overlay]:from-black/20
                [&[data-bookmarked='true']_.thumbnail-overlay]:to-black/20
                [&[data-bookmarked='true']_.thumbnail-overlay]:bg-cover
                [&[data-bookmarked='true']_.thumbnail-overlay]:bg-center
                [&[data-bookmarked='true']_.thumbnail-overlay]:bg-no-repeat`,
                className
            )}
            data-interaction={interaction}
            data-bookmarked={!!content.bookmarked}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <ThumnailBox className="flex-shrink-0">
                <ContentThumbnailImg
                    className="rounded-xl transition-transform duration-300
                        ease-out group-hover:scale-105"
                    src={content.imgurl ?? ""}
                    priority={priority}
                    unoptimized
                    fill={true}
                    onFallbackChange={setShowFallback}
                />
                {!showFallback && (
                    <ThumnailOverlay
                        className={cn(
                            "thumbnail-overlay",
                            showFallback && "hidden"
                        )}
                    />
                )}
                {showBookmark && (
                    <div
                        className="animate-button-slide-in-delay-1 absolute
                            right-2.5 top-2.5 z-[1] opacity-0"
                    >
                        <ContentBookmark variant="filled" content={content} />
                    </div>
                )}
            </ThumnailBox>
            <ContentSourceAuthorHeader
                sourceImgSize={24}
                sourceImgUrl={content.mix_source_info.imgurl}
                authorname={content.authorname}
                updatetime={content.updatetime}
                offlinetime={content.offlinetime}
                category={content.category}
            />
            <div className="mb-4 mt-3 flex min-h-0 gap-3">
                <div className="flex flex-col gap-1 overflow-hidden">
                    <ContentTitle>{content.title}</ContentTitle>
                    <ContentAbstract>
                        {content.abstractcontents}
                    </ContentAbstract>
                </div>
            </div>
            <ContentTags tags={content.mix_content_tags} />
        </ContentCardLayout>
    );
};
